import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LangInput from "@/components/LangInput";
import CollectionsManager from "@/components/CollectionsManager";
import {
  PenNib, Sparkle, ChatCircleDots, Question, PaperPlaneTilt, ArrowRight,
  Users, Certificate, Books,
} from "@phosphor-icons/react";

const LEVEL_LABEL = {
  expert: "NaturePulse Expert",
  trusted: "Trusted Expert",
  verified: "Verified Expert",
  institutional: "Institutional Expert",
  fellow: "NaturePulse Fellow",
};

export default function ExpertDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [questionStatus, setQuestionStatus] = useState("open");
  const [profileForm, setProfileForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [research, setResearch] = useState({ title: "", body: "", topics: "", species: "" });
  const [researchSubmitting, setResearchSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [answerFor, setAnswerFor] = useState(null);
  const [answerText, setAnswerText] = useState("");
  const [convertToArticle, setConvertToArticle] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [collectionsCount, setCollectionsCount] = useState(0);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/"); return; }
    http.get("/experts/me/profile").then(({ data }) => {
      setProfile(data);
      if (data?.expert) {
        const ep = data.expert;
        setProfileForm({
          display_name: ep.display_name || ep.name || "",
          professional_title: ep.professional_title || "",
          institution: ep.institution || "",
          short_bio: ep.short_bio || "",
          full_bio: ep.full_bio || "",
          expertise: (ep.expertise || []).join(", "),
          languages: (ep.languages || []).join(", "),
          country: ep.location?.country || "",
          state: ep.location?.state || "",
          city: ep.location?.city || "",
          accepting_questions: ep.accepting_questions !== false,
          accepting_followers: ep.accepting_followers !== false,
        });
      }
    }).catch(() => {});
  }, [user, loading, nav]);

  useEffect(() => {
    if (!profile?.expert?.slug) return;
    http.get(`/experts/${profile.expert.slug}/posts`, { params: { limit: 12 } })
      .then(({ data }) => setPosts(data.posts || []))
      .catch(() => setPosts([]));
    http.get("/experts/me/collections")
      .then(({ data }) => setCollectionsCount((data.collections || []).length))
      .catch(() => {});
    refreshResearch();
  }, [profile?.expert?.slug]);

  useEffect(() => {
    if (!profile?.is_expert) return;
    http.get("/experts/me/questions", { params: { status: questionStatus } })
      .then(({ data }) => setQuestions(data.questions || []))
      .catch(() => setQuestions([]));
  }, [questionStatus, profile?.is_expert]);

  const refreshResearch = async () => {
    try {
      const { data } = await http.get("/experts/research-requests", { params: { limit: 50, status: "all" } });
      setMyRequests((data.requests || []).filter((r) => r.expert_slug === profile?.expert?.slug));
    } catch { /* noop */ }
  };

  const saveProfile = async () => {
    if (!profileForm) return;
    setSaving(true);
    try {
      const payload = {
        display_name: profileForm.display_name || undefined,
        professional_title: profileForm.professional_title || undefined,
        institution: profileForm.institution || undefined,
        short_bio: profileForm.short_bio || undefined,
        full_bio: profileForm.full_bio || undefined,
        expertise: profileForm.expertise.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 20),
        languages: profileForm.languages.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 12),
        location: {
          country: profileForm.country || undefined,
          state: profileForm.state || undefined,
          city: profileForm.city || undefined,
        },
        accepting_questions: profileForm.accepting_questions,
        accepting_followers: profileForm.accepting_followers,
      };
      await http.patch("/experts/me/profile", payload);
      toast.success("Profile updated");
      // Refresh full profile so read-only side reflects saved data.
      const { data } = await http.get("/experts/me/profile");
      setProfile(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const createResearchRequest = async () => {
    if (research.title.length < 6 || research.body.length < 20) {
      return toast.error("Title (≥6) and description (≥20 chars) are required.");
    }
    setResearchSubmitting(true);
    try {
      await http.post("/experts/me/research-requests", {
        title: research.title.trim(),
        body: research.body.trim(),
        topics: research.topics.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 8),
        species: research.species.trim() || undefined,
      });
      toast.success("Research request posted");
      setResearch({ title: "", body: "", topics: "", species: "" });
      refreshResearch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post request"); }
    finally { setResearchSubmitting(false); }
  };

  const submitAnswer = async () => {
    if (!answerFor || answerText.trim().length < 6) return;
    setAnswering(true);
    try {
      await http.post(`/experts/questions/${answerFor.id}/answer`, {
        answer: answerText.trim(),
        convert_to_article: convertToArticle,
      });
      toast.success(convertToArticle ? "Answer published as an article" : "Answer sent");
      setAnswerFor(null); setAnswerText(""); setConvertToArticle(false);
      // Reload questions.
      const { data } = await http.get("/experts/me/questions", { params: { status: questionStatus } });
      setQuestions(data.questions || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not answer"); }
    finally { setAnswering(false); }
  };

  // Not an expert yet → invite to apply.
  if (profile && !profile.is_expert) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10">
        <div className="max-w-[720px] mx-auto rounded-2xl bg-white border border-black/10 p-8 md:p-10 shadow-sm">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            / Expert Space
          </div>
          <h1 className="mt-2 font-serif-display text-4xl">You don't have an Expert Space yet.</h1>
          <p className="mt-3 text-sm text-[#3A3830]">
            NaturePulse Experts are approved scientists, farmers, naturalists, teachers, photographers and community observers who publish and answer questions across the network.
          </p>
          <Link to="/experts/apply" data-testid="dashboard-apply-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase">
            <Sparkle size={12} weight="fill" /> Apply to become an expert
          </Link>
        </div>
      </main>
    );
  }

  if (!profile || !profileForm) {
    return <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5"><div className="max-w-[900px] mx-auto animate-pulse h-64 bg-white/60 rounded-2xl" /></main>;
  }

  const e = profile.expert;
  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="expert-dashboard">
      <div className="max-w-[1100px] mx-auto space-y-8">

        {/* Header */}
        <section className="rounded-2xl bg-white border border-black/10 p-6 md:p-7 shadow-sm">
          <div className="flex items-center gap-4 flex-wrap">
            <PenNib size={28} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
            <div className="flex-1 min-w-[200px]">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                {LEVEL_LABEL[e.level] || "NaturePulse Expert"}
                {e.verified && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 px-1.5 py-[1px] text-[9px]"><Certificate size={9} weight="fill" /> Verified</span>}
              </div>
              <h1 className="mt-1 font-serif-display text-3xl md:text-4xl leading-tight" data-testid="dashboard-name">
                {e.display_name || e.name}'s Expert Space
              </h1>
            </div>
            <div className="flex gap-2">
              <Link to={`/expert/${e.slug}`} data-testid="dashboard-view-public" className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
                View public space <ArrowRight size={11} weight="bold" />
              </Link>
              <Link to="/stories/new" data-testid="dashboard-new-post" className="inline-flex items-center gap-1 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90">
                <Sparkle size={11} weight="fill" /> New post
              </Link>
            </div>
          </div>
        </section>

        {/* Stats row */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dashboard-stats">
          <StatCard label="Published" value={posts.length} />
          <StatCard label="Open questions" value={questions.filter((q) => q.status === "open").length} />
          <StatCard label="Research requests" value={myRequests.length} />
          <StatCard label="Collections" value={collectionsCount} />
          <StatCard label="Level" value={LEVEL_LABEL[e.level] || "Expert"} small />
        </section>

        {/* Profile editor */}
        <section className="rounded-2xl bg-white border border-black/10 p-6 shadow-sm" data-testid="dashboard-profile-editor">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-muted,#3A3830)]">Edit your profile</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Text field="display_name" label="Display name" v={profileForm} setV={setProfileForm} />
            <Text field="professional_title" label="Professional title" v={profileForm} setV={setProfileForm} />
            <Text field="institution" label="Institution" v={profileForm} setV={setProfileForm} />
            <Text field="expertise" label="Expertise (comma-separated)" v={profileForm} setV={setProfileForm} />
            <Text field="languages" label="Languages (comma-separated)" v={profileForm} setV={setProfileForm} />
            <div className="grid grid-cols-3 gap-2">
              <Text field="country" label="Country" v={profileForm} setV={setProfileForm} />
              <Text field="state" label="State" v={profileForm} setV={setProfileForm} />
              <Text field="city" label="City" v={profileForm} setV={setProfileForm} />
            </div>
          </div>
          <div className="mt-3">
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Short bio (1-2 lines)</label>
            <div className="mt-1">
              <LangInput as="textarea" rows={2} value={profileForm.short_bio || ""} onChange={(e2) => setProfileForm({ ...profileForm, short_bio: e2.target.value })} testid="dashboard-short-bio" />
            </div>
          </div>
          <div className="mt-3">
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Full bio</label>
            <div className="mt-1">
              <LangInput as="textarea" rows={6} value={profileForm.full_bio || ""} onChange={(e2) => setProfileForm({ ...profileForm, full_bio: e2.target.value })} testid="dashboard-full-bio" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-3">
            <Toggle label="Accepting questions" checked={profileForm.accepting_questions} onChange={(v) => setProfileForm({ ...profileForm, accepting_questions: v })} testid="toggle-questions" />
            <Toggle label="Accepting followers" checked={profileForm.accepting_followers} onChange={(v) => setProfileForm({ ...profileForm, accepting_followers: v })} testid="toggle-followers" />
          </div>
          <div className="mt-4">
            <button onClick={saveProfile} disabled={saving} data-testid="dashboard-save-profile" className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase disabled:opacity-50">
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </section>

        {/* Recent posts */}
        <section>
          <div className="flex items-center justify-between">
            <h2 className="font-serif-display text-2xl">Your recent posts</h2>
            <Link to="/stories/new" className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">+ New post</Link>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {posts.length === 0 && <div className="text-sm text-[#3A3830]">Nothing yet — start with an article or a link.</div>}
            {posts.map((p) => (
              <Link key={p.id} to={`/stories/${p.id}`} data-testid={`dashboard-post-${p.id}`} className="rounded-2xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)]">
                <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  {(p.story_type || "").replace(/_/g, " ")} · {p.moderation_status}
                </div>
                <div className="mt-1 font-serif-display text-base line-clamp-2">{p.title || p.headline}</div>
                {p.summary && <div className="mt-1 text-xs text-[#3A3830] line-clamp-2">{p.summary}</div>}
              </Link>
            ))}
          </div>
        </section>

        {/* Collections manager */}
        <CollectionsManager expertSlug={e.slug} ownPosts={posts} />

        {/* Ask-an-expert inbox */}
        <section className="rounded-2xl bg-white border border-black/10 p-6 shadow-sm" data-testid="dashboard-questions">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-serif-display text-2xl flex items-center gap-2"><Question size={20} weight="fill" /> Ask an expert · inbox</h2>
            <div className="flex gap-1.5">
              {["open", "answered", "all"].map((s) => (
                <button key={s} onClick={() => setQuestionStatus(s)} data-testid={`questions-status-${s}`} className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase border ${questionStatus === s ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "border-black/15 bg-white hover:bg-black/5"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {questions.length === 0 && <div className="text-sm text-[#3A3830]">No questions yet.</div>}
            {questions.map((q) => (
              <div key={q.id} data-testid={`question-${q.id}`} className="rounded-lg border border-black/10 p-3 bg-[#FBF2E1]/40">
                <div className="text-xs text-[#3A3830]">from {q.asker_name} · {new Date(q.created_at).toLocaleDateString()}</div>
                <div className="mt-1 font-serif-display text-base">{q.question}</div>
                {q.answer ? (
                  <div className="mt-2 rounded bg-white border border-black/10 p-2 text-sm text-[#3A3830]"><span className="font-medium">Your answer:</span> {q.answer}</div>
                ) : (
                  <button onClick={() => { setAnswerFor(q); setAnswerText(""); setConvertToArticle(false); }} data-testid={`answer-btn-${q.id}`} className="mt-2 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Answer</button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Research request creator */}
        <section className="rounded-2xl bg-white border border-black/10 p-6 shadow-sm" data-testid="dashboard-research">
          <h2 className="font-serif-display text-2xl flex items-center gap-2"><Users size={20} weight="fill" /> Research requests</h2>
          <p className="mt-1 text-sm text-[#3A3830]">Post a request for community observations — the request appears on <Link to="/experts/research-requests" className="text-[color:var(--np-brand,#B85F2A)] underline">the Research Requests hub</Link>.</p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Text field="title" label="Title" v={research} setV={setResearch} />
            <Text field="species" label="Species (optional)" v={research} setV={setResearch} />
          </div>
          <Text field="topics" label="Topics (comma-separated)" v={research} setV={setResearch} className="mt-3" />
          <div className="mt-3">
            <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">What are you looking for?</label>
            <div className="mt-1"><LangInput as="textarea" rows={3} value={research.body} onChange={(ev) => setResearch({ ...research, body: ev.target.value })} testid="research-body" /></div>
          </div>
          <div className="mt-3">
            <button onClick={createResearchRequest} disabled={researchSubmitting} data-testid="research-submit" className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase disabled:opacity-50">
              <PaperPlaneTilt size={11} weight="fill" /> {researchSubmitting ? "Posting…" : "Post request"}
            </button>
          </div>
          {myRequests.length > 0 && (
            <div className="mt-4 pt-4 border-t border-black/10 space-y-2">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Your active requests</div>
              {myRequests.map((r) => (
                <div key={r.id} className="rounded-lg border border-black/10 p-3 bg-[#FBF2E1]/40">
                  <div className="font-serif-display text-base">{r.title}</div>
                  <div className="text-xs text-[#3A3830] mt-1">{r.body}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Answer modal */}
      {answerFor && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" data-testid="answer-modal">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Answer</div>
            <h3 className="mt-1 font-serif-display text-xl">{answerFor.question}</h3>
            <div className="mt-3"><LangInput as="textarea" rows={6} value={answerText} onChange={(ev) => setAnswerText(ev.target.value)} testid="answer-text" /></div>
            <label className="mt-3 flex items-center gap-2 text-xs text-[#3A3830]">
              <input type="checkbox" checked={convertToArticle} onChange={(ev) => setConvertToArticle(ev.target.checked)} data-testid="convert-to-article" />
              Publish this answer as an Expert Article
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setAnswerFor(null)} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Cancel</button>
              <button onClick={submitAnswer} disabled={answering} data-testid="answer-send" className="rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
                {answering ? "Sending…" : "Send answer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

const StatCard = ({ label, value, small }) => (
  <div className="rounded-2xl bg-white border border-black/10 p-4">
    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
    <div className={`mt-1 font-serif-display ${small ? "text-lg" : "text-3xl"} tracking-tight`}>{value ?? "—"}</div>
  </div>
);

const Text = ({ field, label, v, setV, className = "" }) => (
  <div className={className}>
    <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">{label}</label>
    <input value={v[field] || ""} onChange={(e) => setV({ ...v, [field]: e.target.value })} data-testid={`field-${field}`} className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
    {label}
  </label>
);
